import React, { useState } from 'react'
import styled from 'styled-components'
import dynamic from 'next/dynamic'
import { Column, Row } from 'components/common/layout'
import { StartupPositionForSearchNoRequirePosition } from 'types/common'
import { useStores } from 'stores/globals'
import { useIsDesktop } from 'utils/hooks'
import { useRouter } from 'next/router'
import { PositionButton } from 'components/positions/position-button'
import { analytics } from 'infra/analytics'
import { ShadowCard } from 'components/common/card'
import {
  InterestActionLabel,
  InterestActionLabelType,
} from 'components/scouts/common/interest-action-label'
import { DeactivatedStatusModal } from 'components/misc/deactivated-status'
import { Badge, BadgeColor, NewBadge } from 'components/common/badge'
import TagManager from 'react-gtm-module'
import {
  updateStartupPositionInterested,
  viewStartupPosition,
} from 'utils/handlers'
import { StartupPositionItemSimpleInfo } from 'components/positions/startup-position-item-simple-info'
import { StartupSimpleInfo } from 'components/positions/startup-simple-info'
import { PositionFilterStore } from 'stores/position-filter'
import { RoutePaths } from 'types/router'

const StartupVideo = dynamic(() => import('./startup-video'), {
  ssr: false,
})

export const StartupPositionItem: React.FC<{
  data: StartupPositionForSearchNoRequirePosition
  positionFilterStore?: PositionFilterStore
  lastItemRef?: (node: HTMLDivElement) => void
}> = ({ data, positionFilterStore, lastItemRef }) => {
  const [isDeactivatedModalOpen, setIsDeactivatedModalOpen] = useState(false)
  const { startupModalStore, positionModalStore } = useStores()
  const router = useRouter()
  const isDesktop = useIsDesktop()
  const isPositionsPage = router.pathname.includes('/positions') // 채용공고 페이지와 조건에 딱맞는 채용공고 페이지 모두 포함
  const isMatchedPage = router.pathname.includes(RoutePaths.POSITIONS_MATCHED)
  const isInterestedInMe = router.asPath.includes(RoutePaths.INTERESTED_IN_ME)
  const APPLY_EVENT_TRACK = isInterestedInMe
    ? 'itm_jsu_click_applyJobPostBtn'
    : 'click_fp_position_apply_btn'
  const POSITION_SAVE_EVENT_TRACK = isInterestedInMe
    ? 'itm_jsu_click_saveJobPostBtn'
    : 'click_fp_position_save_btn'
  const POSITION_EVENT_TRACK = 'click_fp_position'
  const STARTUP_EVENT_TRACK = 'jobSearch_jsu_click_compDetail'
  return (
    <StartupPositionContainer
      ref={lastItemRef}
      isActivelyHireAds={data.isActivelyHireAds}
    >
      {isPositionsPage && !!data.matchRate && !positionFilterStore?.isEmpty && (
        <MatchRateBadge
          color={data.matchRate === 100 ? BadgeColor.PRIMARY : BadgeColor.GRAY2}
          style={{ position: 'absolute', top: -12, left: -10, zIndex: 1 }}
        >
          {data.matchRate}% 일치
        </MatchRateBadge>
      )}
      {!isPositionsPage && data.startup.isNew && (
        <NewBadge
          style={{ position: 'absolute', top: -7, left: -5, zIndex: 1 }}
        >
          NEW
        </NewBadge>
      )}
      {(data.startup.savedMeAt ||
        data.startup.viewedMeAt ||
        data.hasCuration) && (
        <InterestActionLabel
          onClick={() => {
            if (data.hasCuration)
              router.push({
                pathname: '/my-curation',
                query: { startupId: data.startup.id },
              })
          }}
          actionType={
            data.hasCuration
              ? InterestActionLabelType.HAS_CURATION
              : data.startup.savedMeAt
              ? InterestActionLabelType.STARTUP_SAVED_ME
              : InterestActionLabelType.STARTUP_VIEWED_ME
          }
          time={data.startup.savedMeAt || data.startup.viewedMeAt || ''}
        />
      )}
      <StartupContainer
        id='startup'
        onClick={() => {
          isDesktop
            ? startupModalStore.openModal(data.startup.id)
            : router.push(`/startups/${data.startup.id}`)
          if (
            data.id &&
            isMatchedPage &&
            positionFilterStore?.isViewed === false
          ) {
            // 조건에 딱 맞는 채용 공고 페이지에서 "읽지 않은 채용 공고" 탭의 기업 정보 섹션을 클릭한 경우에도 채용공고를 읽은 것으로 처리함
            viewStartupPosition(data.id)
          }
          analytics.track(STARTUP_EVENT_TRACK, {
            startupId: data.startup.id,
            startupName: data.startup.name,
          })
        }}
      >
        <StartupSimpleInfoWrapper id='startupInfo'>
          <StartupSimpleInfo startup={data.startup} />
        </StartupSimpleInfoWrapper>
        {(data.startup.pitch || data.startup.pt) && (
          <StartupVideo pitch={data.startup.pitch} pt={data.startup.pt} />
        )}
      </StartupContainer>
      {data.id &&
        data.createdAt &&
        data.publishedAt &&
        data.techStacks &&
        data.careerType &&
        data.techStacks && (
          <PositionContainer
            id='position'
            onClick={() => {
              isDesktop
                ? positionModalStore.openModal(data.id || 0)
                : router.push(`/positions/${data.id}`)
              analytics.track(POSITION_EVENT_TRACK, {
                positionId: data.id,
                positionName: data.name,
                startupId: data.startup.id,
                startupName: data.startup.name,
              })
            }}
          >
            <Column style={{ gap: 10, flex: 1 }}>
              <StartupPositionItemSimpleInfo
                position={{
                  careerType: data.careerType || '',
                  experienceRange: data.experienceRange || null,
                  techStacks: data.techStacks || [],
                  publishedAt: data.publishedAt || '',
                  averageReplyPeriod: data.averageReplyPeriod || null,
                  isSincerityReview: data.isSincerityReview || false,
                  name: data.name || '',
                  isActivelyHireAds: data.isActivelyHireAds || false,
                }}
              />
            </Column>
            <Responsive style={{ gap: 8 }}>
              <ButtonContainer onClick={(e) => e.stopPropagation()}>
                <PositionButton
                  data={data}
                  openDeactivatedModal={() => setIsDeactivatedModalOpen(true)}
                  onSaveClick={(bool) => {
                    updateStartupPositionInterested(data.id || 0, !bool)
                    if (!bool) {
                      TagManager.dataLayer({
                        dataLayer: {
                          event: 'btn_click_saveinterest',
                          startup: data.startup.name,
                        },
                      })
                    }
                    analytics.track(POSITION_SAVE_EVENT_TRACK, {
                      positionId: data.id,
                      positionName: data.name,
                      startupId: data.startup.id,
                      startupName: data.startup.name,
                    })
                  }}
                  onApplyClick={() =>
                    analytics.track(APPLY_EVENT_TRACK, {
                      positionId: data.id,
                      positionName: data.name,
                      startupId: data.startup.id,
                      startupName: data.startup.name,
                    })
                  }
                />
              </ButtonContainer>
              {isDeactivatedModalOpen && (
                <DeactivatedStatusModal
                  onClose={() => setIsDeactivatedModalOpen(false)}
                />
              )}
            </Responsive>
          </PositionContainer>
        )}
    </StartupPositionContainer>
  )
}

const MatchRateBadge = styled(Badge)`
  border-radius: 16px;
`

const StartupPositionContainer = styled(ShadowCard)<{
  isActivelyHireAds?: boolean
}>`
  border-radius: 8px;
  border: ${(p) =>
    p.isActivelyHireAds
      ? '1.5px solid #2ECECD'
      : `1px solid ${p.theme.color.line}`};
  position: relative;
`

const StartupContainer = styled(Row)`
  @media ${(p) => p.theme.deviceSize.desktop} {
    align-items: start;
  }
  @media ${(p) => p.theme.deviceSize.mobile} {
    padding: 20px;
  }
`

const StartupSimpleInfoWrapper = styled(Column)`
  flex: 1;
  justify-content: flex-start;
  cursor: pointer;
  gap: 20px;
  :hover {
    .clickable {
      text-decoration: underline;
    }
  }
  @media ${(p) => p.theme.deviceSize.mobile} {
    gap: 10px;
  }
  @media ${(p) => p.theme.deviceSize.desktop} {
    padding: 24px;
  }
`

const Responsive = styled(Row)`
  @media ${(p) => p.theme.deviceSize.mobile} {
    flex-direction: column;
    align-items: stretch;
  }
`

const PositionContainer = styled(Responsive)`
  border-top: 1px solid ${(p) => p.theme.color.line};
  gap: 24px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    padding: 20px;
    gap: 10px;
  }
  @media ${(p) => p.theme.deviceSize.desktop} {
    justify-content: space-between;
    align-items: flex-end;
    padding: 24px;
  }
  :hover {
    cursor: pointer;
    .clickable {
      text-decoration: underline;
    }
  }
`

export const ButtonContainer = styled.div`
  @media ${(p) => p.theme.deviceSize.desktop} {
    width: 274px;
  }
`
